import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleDashed,
  Download,
  History,
  Info,
  Loader2,
  PackagePlus,
  Plus,
  RotateCcw,
  Scale,
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { confirmar } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import {
  cantidadDe,
  formatoCantidad,
  formatoFechaBolivia,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { MarcoDeComercia } from '../components/marco-de-comercia';
import { EncabezadoDeComercia } from '../components/encabezado-de-comercia';
import { resumenDeCarga } from '../components/pasos-de-logistica';
import { comerciaService } from '../services/comercia-service';
import { useImportacion } from '../store/importacion-store';
import type {
  CostoDeLaImportacion,
  ImportacionAnterior,
  ImportacionDeComercia,
} from '../types/comercia.types';
import { m3, siglaDeUnidad, sobreElFob, usd, usdExacto } from '../utils/formatos';
import { ciudadDe } from '../utils/lugares';

/**
 * Comercia › Costo nacionalizado (reunión del 27-oct): «tu mercadería costaba
 * 46.000, tuviste que pagar el flete y el seguro, y aparte tu arancel, tu IVA,
 * tasas. Tu costo nacionalizado es 66.000, y te lo puedo dar por producto porque
 * te lo voy a distribuir» — por el volumen que ocupa cada uno.
 *
 * Desde acá los costos pasan a Datos Maestros, y Cotiza los ofrece al elegir el
 * producto: «ya no hay que hacer esto del porcentaje de flete ni la
 * nacionalización, ya lo jala, sólo ponele margen».
 */
export function CostoNacionalizadoPage() {
  return (
    <MarcoDeComercia>
      <Contenido />
    </MarcoDeComercia>
  );
}

function Contenido() {
  const { importacion, aplicar, cargar } = useImportacion();
  const { toast } = useToast();
  const navegar = useNavigate();
  const [costo, setCosto] = useState<CostoDeLaImportacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [anteriores, setAnteriores] = useState<ImportacionAnterior[] | null>(null);

  const leer = useCallback(async () => {
    if (!importacion) return;
    setError(null);
    try {
      setCosto(await comerciaService.costo());
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo calcular el costo. Vuelve a intentar.'));
    }
  }, [importacion]);

  useEffect(() => {
    void leer();
  }, [leer]);

  useEffect(() => {
    comerciaService
      .anteriores()
      .then(setAnteriores)
      .catch(() => setAnteriores([]));
  }, [importacion?.id]);

  const calculo = costo?.calculo ?? null;

  const publicar = async () => {
    setPublicando(true);
    try {
      const r = await comerciaService.publicar();
      setCosto((c) => (c ? { ...c, publicadoAt: r.publicadoAt } : c));
      await cargar();
      toast({
        title: 'Costos en Datos Maestros',
        description: `${cantidadDe(r.materiales, 'producto quedó', 'productos quedaron')} con su costo nacionalizado. Cotiza lo ofrece al elegirlo.`,
      });
    } catch (e) {
      toast({
        title: 'No se pudieron pasar los costos',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
    } finally {
      setPublicando(false);
    }
  };

  const nueva = async () => {
    const ok = await confirmar({
      title: '¿Empezar otra importación?',
      description:
        'Esta queda guardada en «Importaciones anteriores»: la puedes volver a abrir cuando quieras. Sus productos siguen en tu catálogo.',
      confirmText: 'Empezar otra',
    });
    if (!ok) return;
    try {
      aplicar(await comerciaService.nueva());
      toast({ title: 'Importación nueva', description: 'Empieza por los productos.' });
      navegar('/comercia/productos');
    } catch (e) {
      toast({
        title: 'No se pudo empezar otra',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
    }
  };

  const retomar = async (a: ImportacionAnterior) => {
    const ok = await confirmar({
      title: `¿Volver a abrir ${a.numero}?`,
      description:
        importacion && importacion.productos.length > 0
          ? `La que tienes abierta (${importacion.numero}) pasa a «Importaciones anteriores».`
          : 'La que tienes abierta está vacía y se descarta.',
      confirmText: 'Abrir',
    });
    if (!ok) return;
    try {
      aplicar(await comerciaService.retomar(a.id));
      toast({ title: `${a.numero} abierta` });
    } catch (e) {
      toast({
        title: 'No se pudo abrir',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
    }
  };

  const acumulado = importacion
    ? {
        total: calculo ? calculo.total : importacion.resumen.acumulado,
        fob: importacion.resumen.fob,
        moneda: importacion.moneda,
      }
    : { total: 0, fob: 0 };

  return (
    <>
      <EncabezadoDeComercia
        titulo="Costo nacionalizado"
        subtitulo={
          importacion?.destino
            ? `Lo que te cuesta de verdad la importación, puesta en ${ciudadDe(importacion.destino)}, y cuánto le toca a cada producto.`
            : 'Lo que te cuesta de verdad la importación, y cuánto le toca a cada producto.'
        }
        acumulado={acumulado}
      />

      {importacion && <LineaDeLaImportacion importacion={importacion} />}

      {!importacion || importacion.productos.length === 0 ? (
        <section className="rounded-tarjeta border border-dashed bg-card px-6 py-12 text-center">
          <h2 className="font-titulo text-xl font-semibold">Todavía no hay nada que calcular</h2>
          <p className="mt-1 text-muted-foreground">Empieza por los productos que quieres traer.</p>
          <Link
            to="/comercia/productos"
            className={buttonVariants({ variant: 'degradado', className: 'mt-5 px-6' })}
          >
            Ir a Productos
          </Link>
        </section>
      ) : error ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-tarjeta border border-bloqueo-borde bg-bloqueo-suave px-5 py-4 text-sm text-bloqueo"
        >
          <span className="flex-1">{error}</span>
          <Button size="sm" variant="outline" onClick={() => void leer()}>
            Reintentar
          </Button>
        </div>
      ) : !costo ? (
        <div className="flex items-center gap-3 rounded-tarjeta border bg-card px-6 py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Calculando el costo con el arancel oficial…
        </div>
      ) : !costo.listo || !calculo ? (
        <Faltan faltan={costo.faltan} />
      ) : (
        <>
          <ComoSeArma
            calculo={calculo}
            destino={importacion.destino ? ciudadDe(importacion.destino) : 'destino'}
          />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section
              aria-label="Logística"
              className="rounded-tarjeta border bg-card p-6 shadow-sm"
            >
              <h2 className="font-titulo text-lg font-semibold">Logística</h2>
              <p className="text-sm text-muted-foreground">
                {importacion.ruta
                  ? `Vía ${importacion.ruta.via ?? 'directa'} · ${importacion.ruta.transportistas.join(' + ')}, del tarifario.`
                  : ''}
              </p>
              <dl className="mt-4 space-y-2.5 text-[15px]">
                <Renglon
                  nombre="Flete (internacional, puerto y camión)"
                  valor={usd(calculo.logistica.flete)}
                />
                <Renglon
                  nombre="Seguro"
                  valor={usd(calculo.logistica.seguro)}
                  nota="1,5 % sobre FOB + flete"
                />
                {calculo.logistica.otros > 0 && (
                  <Renglon
                    nombre="Servicios en destino"
                    valor={usd(calculo.logistica.otros)}
                    nota="no pagan tributos"
                  />
                )}
                <Renglon nombre="Total logística" valor={usd(calculo.logistica.total)} fuerte />
              </dl>
            </section>

            <section
              aria-label="Tributación"
              className="rounded-tarjeta border bg-card p-6 shadow-sm"
            >
              <h2 className="font-titulo text-lg font-semibold">Tributación</h2>
              <p className="text-sm text-muted-foreground">
                Del arancel oficial vigente, partida por partida, sobre el CIF de cada producto.
              </p>
              <dl className="mt-4 space-y-2.5 text-[15px]">
                <Renglon
                  nombre="Base CIF (FOB + flete + seguro)"
                  valor={usd(calculo.productos.reduce((s, p) => s + p.cif, 0))}
                />
                <Renglon nombre="Gravamen arancelario (GA)" valor={usd(calculo.tributos.ga)} />
                <Renglon
                  nombre="IVA de importación"
                  valor={usd(calculo.tributos.iva)}
                  nota={`${formatoPorcentaje(calculo.tributos.ivaNominal * 100, { decimales: 0 })} por dentro = ${formatoPorcentaje(calculo.tributos.ivaEfectivo * 100, { decimales: 2 })} sobre CIF + GA`}
                />
                <Renglon nombre="Total tributos" valor={usd(calculo.tributos.total)} fuerte />
              </dl>
              {costo.avisos.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {costo.avisos.map((a) => (
                    <li
                      key={a.mensaje}
                      className={cn(
                        'flex gap-2 rounded-lg px-3 py-2 text-sm',
                        a.nivel === 'PROHIBICION'
                          ? 'bg-bloqueo-suave text-bloqueo'
                          : 'bg-alerta-suave text-alerta'
                      )}
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      {a.mensaje}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <PorProducto importacion={importacion} costo={costo} />

          <RepartoPorVolumen calculo={calculo} importacion={importacion} />

          <section
            aria-label="Usar estos costos"
            className="flex flex-col gap-4 rounded-tarjeta border bg-card p-6 shadow-sm lg:flex-row lg:items-center"
          >
            <div className="min-w-0 flex-1">
              <h2 className="font-titulo text-lg font-semibold">Úsalo para cotizar</h2>
              {costo.publicadoAt ? (
                <p className="mt-1 flex items-center gap-2 text-[15px] text-ok">
                  <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                  En Datos Maestros desde el{' '}
                  {formatoFechaBolivia(costo.publicadoAt, { hora: true })}: Cotiza ofrece el costo
                  nacionalizado al elegir cada producto, sin sumarle otra vez flete ni
                  nacionalización.
                </p>
              ) : (
                <p className="mt-1 text-[15px] text-muted-foreground">
                  Pasa el costo por unidad de cada producto a Datos Maestros. Al cotizar, lo eliges
                  y sólo le pones el margen.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => descargar(importacion, costo)}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Descargar
              </Button>
              {costo.publicadoAt ? (
                <Link
                  to="/materials"
                  className={buttonVariants({ variant: 'outline', className: 'rounded-full' })}
                >
                  Ver en Materiales
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              ) : null}
              <Button
                variant="degradado"
                className="rounded-full px-6"
                onClick={() => void publicar()}
                disabled={publicando}
              >
                {publicando ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <PackagePlus className="h-4 w-4" aria-hidden="true" />
                )}
                {costo.publicadoAt ? 'Volver a pasar los costos' : 'Usar estos costos en Cotiza'}
              </Button>
            </div>
          </section>
        </>
      )}

      <section
        aria-label="Importaciones"
        className="space-y-3 rounded-tarjeta border bg-card p-6 shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-titulo text-lg font-semibold">
            <History className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            Importaciones anteriores
          </h2>
          {importacion && importacion.productos.length > 0 && (
            <Button variant="outline" className="rounded-full" onClick={() => void nueva()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nueva importación
            </Button>
          )}
        </div>
        {anteriores === null ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : anteriores.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Todavía no cerraste ninguna. Al empezar una nueva, esta queda acá.
          </p>
        ) : (
          <ul className="divide-y">
            {anteriores.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-[15px]"
              >
                <span className="font-semibold">{a.numero}</span>
                <span className="text-muted-foreground">
                  {a.origen && a.destino ? `${a.origen} → ${a.destino}` : 'sin ruta'} ·{' '}
                  {cantidadDe(a.productos, 'producto', 'productos')}
                </span>
                <span className="tabular-nums">FOB {usd(a.fob)}</span>
                <span className="tabular-nums">
                  {a.costoNacionalizado != null
                    ? `Nacionalizado ${usd(a.costoNacionalizado)}`
                    : 'sin costo publicado'}
                </span>
                <span className="text-sm text-muted-foreground">
                  {a.cerradaAt ? formatoFechaBolivia(a.cerradaAt) : ''}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto rounded-full"
                  onClick={() => void retomar(a)}
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Volver a abrir
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="flex items-start gap-2 rounded-2xl bg-muted/50 px-5 py-3.5 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          Es una estimación para decidir precios: la logística sale de tu tarifario y los tributos,
          del arancel oficial vigente. La liquidación de la aduana (DUI) puede variar con el tipo de
          cambio del día y los gastos reales.
        </span>
      </p>
    </>
  );
}

/** «IMP-2026-0088 · Shanghái → Santa Cruz · 1 × 20' estándar · vía Arica». */
function LineaDeLaImportacion({ importacion: imp }: { importacion: ImportacionDeComercia }) {
  const carga = resumenDeCarga(imp);
  const partes = [
    imp.numero,
    imp.origen && imp.destino ? `${ciudadDe(imp.origen)} → ${ciudadDe(imp.destino)}` : null,
    carga,
    imp.ruta && !imp.ruta.desactualizada
      ? `vía ${imp.ruta.via ?? 'directa'} · ${imp.ruta.transportistas.join(' + ')}`
      : null,
  ].filter(Boolean);
  return (
    <p className="rounded-full border bg-card px-5 py-2.5 text-sm text-muted-foreground shadow-sm">
      <span className="font-semibold text-foreground">Importación en curso</span> ·{' '}
      {partes.join(' · ')}
    </p>
  );
}

function Faltan({ faltan }: { faltan: string[] }) {
  const destino = (f: string) =>
    /producto|medidas/i.test(f)
      ? '/comercia/productos'
      : /origen|destino/i.test(f) && !/carga/i.test(f)
        ? '/comercia/logistica/origen-destino'
        : /carga|contenedor/i.test(f)
          ? '/comercia/logistica/carga'
          : '/comercia/logistica/rutas';
  return (
    <section className="rounded-tarjeta border bg-card p-6 shadow-sm">
      <h2 className="font-titulo text-xl font-semibold">Falta poco para el costo nacionalizado</h2>
      <ul className="mt-4 space-y-2.5">
        {faltan.map((f) => (
          <li key={f} className="flex flex-wrap items-center gap-3 text-[15px]">
            <CircleDashed className="h-5 w-5 shrink-0 text-alerta" aria-hidden="true" />
            <span className="flex-1">{f}</span>
            <Link to={destino(f)} className="text-sm font-semibold text-primary hover:underline">
              Resolver
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Mercadería + Logística + Tributación = Costo nacionalizado, con su barra. */
function ComoSeArma({
  calculo,
  destino,
}: {
  calculo: NonNullable<CostoDeLaImportacion['calculo']>;
  destino: string;
}) {
  const partes = [
    { nombre: 'Mercadería', nota: 'valor FOB', valor: calculo.mercaderia, clase: 'bg-primary' },
    {
      nombre: 'Logística',
      nota: 'flete, seguro y gastos',
      valor: calculo.logistica.total,
      clase: 'bg-orange-700',
    },
    {
      nombre: 'Tributación',
      nota: 'arancel e IVA',
      valor: calculo.tributos.total,
      clase: 'bg-prixia-sky',
    },
  ];
  return (
    <section
      aria-label="Cómo se arma el costo"
      className="rounded-tarjeta border bg-card p-6 shadow-sm"
    >
      <h2 className="font-titulo text-xl font-semibold">Cómo se arma tu costo</h2>
      <div className="mt-5 grid items-stretch gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.2fr]">
        {partes.map((p, i) => (
          <Bloque
            key={p.nombre}
            signo={i > 0 ? '+' : null}
            nombre={p.nombre}
            nota={p.nota}
            valor={usd(p.valor)}
            punto={p.clase}
          />
        ))}
        <span
          className="hidden self-center text-2xl font-semibold text-muted-foreground lg:block"
          aria-hidden="true"
        >
          =
        </span>
        <div className="rounded-2xl bg-degradado-prixia p-5 text-white shadow-md">
          <p className="text-sm font-semibold text-white/85">Costo nacionalizado</p>
          <p className="mt-1 font-titulo text-[30px] font-semibold leading-none tabular-nums">
            {usd(calculo.total)}
          </p>
          <p className="mt-2 text-sm text-white/85">
            puesto en {destino} · {sobreElFob(calculo.sobreFob)} sobre el FOB
          </p>
        </div>
      </div>
      <div className="mt-6 flex h-4 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {partes.map((p) => (
          <span
            key={p.nombre}
            className={cn('h-full', p.clase)}
            style={{ width: `${(p.valor / calculo.total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        {partes.map((p) => (
          <li key={p.nombre} className="flex items-center gap-2">
            <span className={cn('h-2.5 w-2.5 rounded-full', p.clase)} aria-hidden="true" />
            {p.nombre} · {formatoPorcentaje((p.valor / calculo.total) * 100, { decimales: 0 })}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Bloque({
  signo,
  nombre,
  nota,
  valor,
  punto,
}: {
  signo: string | null;
  nombre: string;
  nota: string;
  valor: string;
  punto: string;
}): ReactNode {
  return (
    <>
      {signo && (
        <span
          className="hidden self-center text-2xl font-semibold text-muted-foreground lg:block"
          aria-hidden="true"
        >
          {signo}
        </span>
      )}
      <div className="rounded-2xl border bg-surface-sunken p-5">
        <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <span className={cn('h-2.5 w-2.5 rounded-full', punto)} aria-hidden="true" />
          {nombre}
        </p>
        <p className="mt-1 font-titulo text-[26px] font-semibold leading-none tabular-nums">
          {valor}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">{nota}</p>
      </div>
    </>
  );
}

function Renglon({
  nombre,
  valor,
  nota,
  fuerte,
}: {
  nombre: string;
  valor: string;
  nota?: string;
  fuerte?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4',
        fuerte && 'border-t pt-2.5 font-semibold'
      )}
    >
      <dt>
        {nombre}
        {nota && <span className="block text-xs font-normal text-muted-foreground">{nota}</span>}
      </dt>
      <dd className="shrink-0 tabular-nums">{valor}</dd>
    </div>
  );
}

/** La tabla por producto: lo que le toca a cada uno y cuánto cuesta cada unidad puesta en destino. */
function PorProducto({
  importacion,
  costo,
}: {
  importacion: ImportacionDeComercia;
  costo: CostoDeLaImportacion;
}) {
  const calculo = costo.calculo!;
  const productoDe = useMemo(
    () => new Map(importacion.productos.map((p) => [p.id, p])),
    [importacion.productos]
  );
  const partidaDe = useMemo(
    () => new Map(costo.partidas.map((p) => [p.productoId, p])),
    [costo.partidas]
  );
  return (
    <section
      aria-label="Costo por producto"
      className="overflow-hidden rounded-tarjeta border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-6 py-5">
        <h2 className="font-titulo text-xl font-semibold">Por producto</h2>
        <p className="text-sm text-muted-foreground">
          La logística se reparte por el volumen que ocupa cada uno; el seguro, por su valor.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px] text-[15px]">
          <thead className="bg-surface-sunken text-sm text-muted-foreground">
            <tr>
              <th scope="col" className="px-6 py-3 text-left font-semibold">
                Producto
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Cantidad
              </th>
              <th scope="col" className="px-3 py-3 text-left font-semibold">
                Volumen
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                FOB
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Logística
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Tributos
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Nacionalizado
              </th>
              <th scope="col" className="px-6 py-3 text-right font-semibold">
                Por unidad
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {calculo.productos.map((p) => {
              const prod = productoDe.get(p.id);
              const partida = partidaDe.get(p.id);
              return (
                <tr key={p.id}>
                  <td className="max-w-[320px] px-6 py-4">
                    <p className="flex items-center gap-2 font-semibold">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: prod?.color }}
                        aria-hidden="true"
                      />
                      <span className="truncate" title={p.descripcion}>
                        {p.descripcion}
                      </span>
                    </p>
                    <p className="ml-5 text-xs text-muted-foreground">
                      {partida?.codigo ?? 'sin partida'} · arancel{' '}
                      {formatoPorcentaje(partida?.ga ?? 0, { decimalesMaximos: 2 })}
                      {partida && partida.origen !== 'ARANCEL_OFICIAL' && (
                        <span className="text-alerta"> (estimado)</span>
                      )}
                    </p>
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums">
                    {formatoCantidad(p.cantidad)} {siglaDeUnidad(prod?.unidad ?? 'UND')}
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-20 overflow-hidden rounded-full bg-muted"
                        aria-hidden="true"
                      >
                        <span
                          className="block h-full rounded-full bg-orange-700"
                          style={{ width: `${p.partePorVolumen}%` }}
                        />
                      </span>
                      <span className="text-sm tabular-nums">
                        {formatoPorcentaje(p.partePorVolumen, { decimales: 0 })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground tabular-nums">{m3(p.volumen)}</p>
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums">{usd(p.fob)}</td>
                  <td className="px-3 py-4 text-right tabular-nums">{usd(p.logistica)}</td>
                  <td className="px-3 py-4 text-right tabular-nums">{usd(p.tributos)}</td>
                  <td className="px-3 py-4 text-right font-semibold tabular-nums">
                    {usd(p.total)}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums">
                    <p className="font-semibold text-orange-700">{usdExacto(p.unitario)}</p>
                    <p className="text-xs text-muted-foreground">
                      FOB {usdExacto(p.fobUnitario)} · {sobreElFob(p.sobreFob)}
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 bg-surface-sunken/60 font-semibold">
            <tr>
              <td className="px-6 py-3.5">Total</td>
              <td className="px-3 py-3.5 text-right tabular-nums">
                {formatoCantidad(calculo.productos.reduce((s, p) => s + p.cantidad, 0))}
              </td>
              <td className="px-3 py-3.5 tabular-nums">{m3(calculo.volumenTotal)}</td>
              <td className="px-3 py-3.5 text-right tabular-nums">{usd(calculo.mercaderia)}</td>
              <td className="px-3 py-3.5 text-right tabular-nums">
                {usd(calculo.logistica.total)}
              </td>
              <td className="px-3 py-3.5 text-right tabular-nums">{usd(calculo.tributos.total)}</td>
              <td className="px-3 py-3.5 text-right tabular-nums">{usd(calculo.total)}</td>
              <td className="px-6 py-3.5 text-right text-sm text-muted-foreground">
                {sobreElFob(calculo.sobreFob)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/**
 * Por qué importa repartir por volumen (el argumento de la reunión): lo que pagaría
 * cada producto de flete si se dividiera «a ojo» por valor, contra lo que paga
 * por el lugar que ocupa de verdad.
 */
function RepartoPorVolumen({
  calculo,
  importacion,
}: {
  calculo: NonNullable<CostoDeLaImportacion['calculo']>;
  importacion: ImportacionDeComercia;
}) {
  const flete = calculo.logistica.flete + calculo.logistica.otros;
  if (calculo.productos.length < 2 || flete <= 0) return null;
  const colorDe = new Map(importacion.productos.map((p) => [p.id, p.color]));
  const filas = calculo.productos.map((p) => {
    const porVolumen = p.flete + p.otros;
    const porValor = calculo.mercaderia > 0 ? flete * (p.fob / calculo.mercaderia) : 0;
    return { ...p, porVolumen, porValor, diferencia: porVolumen - porValor };
  });
  return (
    <section
      aria-label="Reparto por volumen"
      className="rounded-tarjeta border bg-card p-6 shadow-sm"
    >
      <h2 className="flex items-center gap-2 font-titulo text-lg font-semibold">
        <Scale className="h-5 w-5 text-orange-700" aria-hidden="true" />
        Cada producto paga el flete del lugar que ocupa
      </h2>
      <p className="mt-1 max-w-3xl text-[15px] text-muted-foreground">
        Repartirlo por valor —el mismo porcentaje para todos— hace que lo chico y caro pague el
        espacio de lo grande y barato. Así cambia con el reparto por volumen:
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-[15px]">
          <thead className="text-sm text-muted-foreground">
            <tr>
              <th scope="col" className="py-2 text-left font-semibold">
                Producto
              </th>
              <th scope="col" className="py-2 text-right font-semibold">
                Flete por valor
              </th>
              <th scope="col" className="py-2 text-right font-semibold">
                Flete por volumen
              </th>
              <th scope="col" className="py-2 text-right font-semibold">
                Diferencia
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filas.map((f) => (
              <tr key={f.id}>
                <td className="py-2.5">
                  <span className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: colorDe.get(f.id) }}
                      aria-hidden="true"
                    />
                    {f.descripcion}
                  </span>
                </td>
                <td className="py-2.5 text-right text-muted-foreground tabular-nums">
                  {usd(f.porValor)}
                </td>
                <td className="py-2.5 text-right font-semibold tabular-nums">
                  {usd(f.porVolumen)}
                </td>
                <td
                  className={cn(
                    'py-2.5 text-right tabular-nums',
                    f.diferencia > 0.5
                      ? 'text-alerta'
                      : f.diferencia < -0.5
                        ? 'text-ok'
                        : 'text-muted-foreground'
                  )}
                >
                  {f.diferencia > 0 ? '+' : f.diferencia < 0 ? '−' : ''}
                  {usd(Math.abs(f.diferencia))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** La tabla por producto en un archivo que abre Excel (separado por «;», como lo espera en español). */
function descargar(importacion: ImportacionDeComercia, costo: CostoDeLaImportacion) {
  const calculo = costo.calculo;
  if (!calculo) return;
  const n = (v: number) => formatoPrecio(v);
  const filas = [
    [
      'Producto',
      'Partida',
      'Cantidad',
      'Volumen m3',
      'FOB',
      'Flete',
      'Seguro',
      'Servicios',
      'GA',
      'IVA',
      'Costo nacionalizado',
      'Por unidad',
    ],
    ...calculo.productos.map((p) => [
      p.descripcion,
      costo.partidas.find((x) => x.productoId === p.id)?.codigo ?? '',
      String(p.cantidad),
      n(p.volumen),
      n(p.fob),
      n(p.flete),
      n(p.seguro),
      n(p.otros),
      n(p.ga),
      n(p.iva),
      n(p.total),
      n(p.unitario),
    ]),
    [
      'Total',
      '',
      '',
      n(calculo.volumenTotal),
      n(calculo.mercaderia),
      n(calculo.logistica.flete),
      n(calculo.logistica.seguro),
      n(calculo.logistica.otros),
      n(calculo.tributos.ga),
      n(calculo.tributos.iva),
      n(calculo.total),
      '',
    ],
  ];
  const csv = filas
    .map((f) => f.map((c) => (/[;"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(';'))
    .join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${importacion.numero}-costo-nacionalizado.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
